/**
 * RichEditor — TipTap-based rich text editor for ChatsPulse.
 *
 * Adapted from the SocialPulse PublisherEditor for the standalone
 * ChatsPulse fork. Removes external Unsplash / PhotoPulse / unfurl
 * dependencies; image uploads go through /api/articles/images
 * (Emergent Object Storage backed).
 *
 * Supports:
 *   • Bold / italic / strike / inline code
 *   • H1..H3, bullet + ordered lists, blockquote, code block
 *   • Font family + font size (7 publishing fonts, 9 size steps)
 *   • Text alignment (paragraph)
 *   • Image insertion via URL OR file upload with alignment/wrap
 *     (left, center, right, 4 corners, dropcap)
 *   • Link insertion + edit
 *   • YouTube video embed
 *   • Live transliteration (Latin → Indic script) via
 *     `/api/i18n/transliterate` while typing
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import Youtube from "@tiptap/extension-youtube";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { FontFamily } from "@tiptap/extension-font-family";
import {
  Bold, Italic, Strikethrough, Code, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Undo, Redo, Link as LinkIcon,
  ImageIcon, AlignLeft, AlignCenter, AlignRight, Youtube as YoutubeIcon,
  CornerUpLeft, CornerUpRight, CornerDownLeft, CornerDownRight, Type,
  Languages, ChevronDown, Loader2, Upload,
} from "lucide-react";
import { TransliterateExtension } from "@/components/publisher/TransliterateExtension";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Curated publishing fonts. Keep this list opinionated so authors
// don't get lost in a font zoo — Serif, Sans, Slab, Display, Mono.
const FONT_FAMILIES = [
  { label: "Default",   value: "" },
  { label: "Serif — Georgia",    value: "Georgia, 'Times New Roman', serif" },
  { label: "Serif — Merriweather", value: "Merriweather, Georgia, serif" },
  { label: "Serif — Playfair",   value: "'Playfair Display', Georgia, serif" },
  { label: "Sans — Inter",       value: "Inter, system-ui, sans-serif" },
  { label: "Sans — Helvetica",   value: "'Helvetica Neue', Helvetica, Arial, sans-serif" },
  { label: "Slab — Roboto Slab", value: "'Roboto Slab', Georgia, serif" },
  { label: "Display — Lora",     value: "Lora, Georgia, serif" },
  { label: "Mono — JetBrains",   value: "'JetBrains Mono', ui-monospace, monospace" },
];

const FONT_SIZES = [
  { label: "12", value: "12px" },
  { label: "14", value: "14px" },
  { label: "16 (default)", value: "16px" },
  { label: "18", value: "18px" },
  { label: "20", value: "20px" },
  { label: "24", value: "24px" },
  { label: "28", value: "28px" },
  { label: "32", value: "32px" },
  { label: "40", value: "40px" },
];

// Custom TipTap mark for font-size (TipTap doesn't ship one out-of-the-box).
// We stash it on TextStyle via inline `style="font-size: XX"`.
const FontSize = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: (el) => el.style.fontSize?.replace(/['"]+/g, "") || null,
        renderHTML: (attrs) => {
          if (!attrs.fontSize) return {};
          return { style: `font-size: ${attrs.fontSize}` };
        },
      },
    };
  },
  addCommands() {
    return {
      ...this.parent?.(),
      setFontSize:
        (size) =>
        ({ chain }) =>
          chain().setMark("textStyle", { fontSize: size }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

const TRANSLIT_LANGS = [
  { code: "off", label: "Off", native: "English", short: "EN" },
  { code: "hi",  label: "Hindi",     native: "हिन्दी",   short: "HI" },
  { code: "ml",  label: "Malayalam", native: "മലയാളം",  short: "ML" },
  { code: "ta",  label: "Tamil",     native: "தமிழ்",    short: "TA" },
  { code: "te",  label: "Telugu",    native: "తెలుగు",  short: "TE" },
  { code: "kn",  label: "Kannada",   native: "ಕನ್ನಡ",   short: "KN" },
  { code: "bn",  label: "Bengali",   native: "বাংলা",   short: "BN" },
  { code: "mr",  label: "Marathi",   native: "मराठी",   short: "MR" },
  { code: "gu",  label: "Gujarati",  native: "ગુજરાતી", short: "GU" },
  { code: "pa",  label: "Punjabi",   native: "ਪੰਜਾਬੀ",   short: "PA" },
  { code: "or",  label: "Odia",      native: "ଓଡ଼ିଆ",   short: "OR" },
  { code: "as",  label: "Assamese",  native: "অসমীয়া", short: "AS" },
  { code: "ur",  label: "Urdu",      native: "اردو",    short: "UR" },
];
const TRANSLIT_PREF_KEY = "cp:input-lang";
const TRANSLIT_SUPPORTED_CODES = new Set(TRANSLIT_LANGS.map((l) => l.code));

const ALIGN_VALUES = new Set([
  "left", "center", "right",
  "corner-tl", "corner-tr", "corner-bl", "corner-br",
  "dropcap",
]);

const AlignedImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: "center",
        parseHTML: (el) => {
          const val = el.getAttribute("data-align") || "center";
          return ALIGN_VALUES.has(val) ? val : "center";
        },
        renderHTML: (attrs) => ({
          "data-align": attrs.align,
          class: `pub-align-${attrs.align}`,
        }),
      },
    };
  },
});

function ToolbarBtn({ onClick, active, disabled, title, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-pressed={active}
      className={`w-8 h-8 rounded-md flex items-center justify-center text-sm transition-colors ${
        active ? "bg-blue-600 text-white" : "bg-transparent text-slate-700 hover:bg-slate-100"
      } disabled:opacity-30 disabled:cursor-not-allowed`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div className="w-px h-6 bg-slate-200 mx-1" />;
}

export default function RichEditor({
  value,
  onChange,
  placeholder = "Tell your story…",
  testIdPrefix = "rich-editor",
  authToken = null,
}) {
  const fileInputRef = useRef(null);

  const [translitLang, setTranslitLang] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = window.localStorage.getItem(TRANSLIT_PREF_KEY);
      if (saved && TRANSLIT_SUPPORTED_CODES.has(saved)) return saved;
    }
    return "off";
  });
  const [translitBusy, setTranslitBusy] = useState(false);
  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const translitLangRef = useRef(translitLang);

  useEffect(() => {
    translitLangRef.current = translitLang;
    if (typeof window !== "undefined" && translitLang) {
      window.localStorage.setItem(TRANSLIT_PREF_KEY, translitLang);
    }
  }, [translitLang]);

  const currentTranslitMeta = useMemo(
    () => TRANSLIT_LANGS.find((l) => l.code === translitLang) || TRANSLIT_LANGS[0],
    [translitLang],
  );

  const transliterateWord = useCallback(async (word, lang) => {
    try {
      const r = await fetch(`${API}/i18n/transliterate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({ text: word, target_lang: lang }),
      });
      if (!r.ok) return word;
      const data = await r.json();
      return data?.transliterated || word;
    } catch (_e) {
      return word;
    }
  }, [authToken]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener nofollow", target: "_blank" },
      }),
      AlignedImage,
      Youtube.configure({ inline: false, controls: true, nocookie: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      FontSize,
      FontFamily.configure({ types: ["textStyle"] }),
      Placeholder.configure({ placeholder }),
      TransliterateExtension.configure({
        getLang: () => translitLangRef.current,
        transliterate: transliterateWord,
        onBusy: setTranslitBusy,
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange && onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value != null && value !== current) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const insertImageByUrl = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("Image URL");
    if (!url) return;
    const alt = window.prompt("Alt text (short description for accessibility)", "") || "";
    editor.chain().focus()
      .setImage({ src: url, alt, align: "center" })
      .createParagraphNear()
      .run();
  }, [editor]);

  const insertImageByUpload = useCallback(async (file) => {
    if (!editor || !file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await fetch(`${API}/articles/images`, {
        method: "POST",
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        credentials: "include",
        body: form,
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      const data = await r.json();
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const src = data.url?.startsWith("http") ? data.url : `${backend}${data.url}`;
      editor.chain().focus()
        .setImage({ src, alt: file.name, align: "center" })
        .createParagraphNear()
        .run();
    } catch (e) {
      window.alert(`Image upload failed: ${e.message}`);
    }
  }, [editor, authToken]);

  const setImageAlign = useCallback((align) => {
    if (!editor) return;
    editor.chain().focus().updateAttributes("image", { align }).run();
  }, [editor]);

  const insertYoutube = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("YouTube URL");
    if (!url) return;
    editor.chain().focus().setYoutubeVideo({ src: url, width: 640, height: 360 }).run();
  }, [editor]);

  if (!editor) return null;

  const imgSelected = editor.isActive("image");
  const currentFontFamily = editor.getAttributes("textStyle").fontFamily || "";
  const currentFontSize = editor.getAttributes("textStyle").fontSize || "";

  return (
    <div className="border border-slate-300 rounded-2xl bg-white overflow-hidden" data-testid={testIdPrefix}>
      <div
        className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 p-2 sticky top-0 z-10"
        data-testid={`${testIdPrefix}-toolbar`}
      >
        {/* Font family */}
        <select
          value={currentFontFamily}
          onChange={(e) => {
            const v = e.target.value;
            if (v) editor.chain().focus().setFontFamily(v).run();
            else editor.chain().focus().unsetFontFamily().run();
          }}
          className="h-8 rounded-md border border-slate-200 bg-white text-xs px-1 mr-1 max-w-[150px]"
          data-testid={`${testIdPrefix}-font-family`}
          title="Font family"
        >
          {FONT_FAMILIES.map((f) => (
            <option key={f.label} value={f.value} style={{ fontFamily: f.value || "inherit" }}>
              {f.label}
            </option>
          ))}
        </select>
        {/* Font size */}
        <select
          value={currentFontSize}
          onChange={(e) => {
            const v = e.target.value;
            if (v) editor.chain().focus().setFontSize(v).run();
            else editor.chain().focus().unsetFontSize().run();
          }}
          className="h-8 rounded-md border border-slate-200 bg-white text-xs px-1 mr-1"
          data-testid={`${testIdPrefix}-font-size`}
          title="Font size"
        >
          <option value="">Size</option>
          {FONT_SIZES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <Divider />

        <ToolbarBtn testId={`${testIdPrefix}-bold`} title="Bold"
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}><Bold className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-italic`} title="Italic"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}><Italic className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-strike`} title="Strikethrough"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive("strike")}><Strikethrough className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-code`} title="Inline code"
          onClick={() => editor.chain().focus().toggleCode().run()}
          active={editor.isActive("code")}><Code className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        <ToolbarBtn testId={`${testIdPrefix}-h1`} title="Heading 1"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive("heading", { level: 1 })}><Heading1 className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-h2`} title="Heading 2"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive("heading", { level: 2 })}><Heading2 className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-h3`} title="Heading 3"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive("heading", { level: 3 })}><Heading3 className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        <ToolbarBtn testId={`${testIdPrefix}-ul`} title="Bulleted list"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}><List className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-ol`} title="Numbered list"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}><ListOrdered className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-quote`} title="Blockquote"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive("blockquote")}><Quote className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        <ToolbarBtn testId={`${testIdPrefix}-align-left`} title="Align left"
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          active={editor.isActive({ textAlign: "left" })}><AlignLeft className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-align-center`} title="Align center"
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          active={editor.isActive({ textAlign: "center" })}><AlignCenter className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-align-right`} title="Align right"
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          active={editor.isActive({ textAlign: "right" })}><AlignRight className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        <ToolbarBtn testId={`${testIdPrefix}-link`} title="Insert link"
          onClick={setLink}
          active={editor.isActive("link")}><LinkIcon className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-image-url`} title="Insert image URL"
          onClick={insertImageByUrl}><ImageIcon className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-image-upload`} title="Upload image from device"
          onClick={() => fileInputRef.current?.click()}><Upload className="w-4 h-4" /></ToolbarBtn>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          style={{ display: "none" }}
          data-testid={`${testIdPrefix}-image-file-input`}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) insertImageByUpload(f);
            e.target.value = "";
          }}
        />
        <ToolbarBtn testId={`${testIdPrefix}-youtube`} title="Embed YouTube video"
          onClick={insertYoutube}><YoutubeIcon className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        {imgSelected && (
          <>
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold pr-1">
              Image
            </span>
            <ToolbarBtn testId={`${testIdPrefix}-img-left`} title="Wrap left"
              onClick={() => setImageAlign("left")}
              active={editor.getAttributes("image").align === "left"}><AlignLeft className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn testId={`${testIdPrefix}-img-center`} title="Center"
              onClick={() => setImageAlign("center")}
              active={editor.getAttributes("image").align === "center"}><AlignCenter className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn testId={`${testIdPrefix}-img-right`} title="Wrap right"
              onClick={() => setImageAlign("right")}
              active={editor.getAttributes("image").align === "right"}><AlignRight className="w-4 h-4" /></ToolbarBtn>
            <Divider />
            <ToolbarBtn testId={`${testIdPrefix}-img-corner-tl`} title="Corner: top-left"
              onClick={() => setImageAlign("corner-tl")}
              active={editor.getAttributes("image").align === "corner-tl"}><CornerUpLeft className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn testId={`${testIdPrefix}-img-corner-tr`} title="Corner: top-right"
              onClick={() => setImageAlign("corner-tr")}
              active={editor.getAttributes("image").align === "corner-tr"}><CornerUpRight className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn testId={`${testIdPrefix}-img-corner-bl`} title="Corner: bottom-left"
              onClick={() => setImageAlign("corner-bl")}
              active={editor.getAttributes("image").align === "corner-bl"}><CornerDownLeft className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn testId={`${testIdPrefix}-img-corner-br`} title="Corner: bottom-right"
              onClick={() => setImageAlign("corner-br")}
              active={editor.getAttributes("image").align === "corner-br"}><CornerDownRight className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn testId={`${testIdPrefix}-img-dropcap`} title="Drop-cap lead illustration"
              onClick={() => setImageAlign("dropcap")}
              active={editor.getAttributes("image").align === "dropcap"}><Type className="w-4 h-4" /></ToolbarBtn>
            <Divider />
          </>
        )}
        <ToolbarBtn testId={`${testIdPrefix}-undo`} title="Undo"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}><Undo className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId={`${testIdPrefix}-redo`} title="Redo"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}><Redo className="w-4 h-4" /></ToolbarBtn>

        {/* Live transliteration chip */}
        <div className="ml-auto relative flex items-center gap-1">
          {translitBusy && (
            <Loader2
              className="w-3.5 h-3.5 animate-spin text-slate-400"
              data-testid={`${testIdPrefix}-translit-busy`}
            />
          )}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setLangPickerOpen((v) => !v); }}
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest shadow-sm hover:bg-slate-50"
            style={{ color: translitLang === "off" ? "#6B7280" : "#B58936" }}
            title={translitLang === "off"
              ? "Type in English. Click to enable Indic script."
              : `Live-converting to ${currentTranslitMeta.native}. Click to change.`}
            data-testid={`${testIdPrefix}-translit-chip`}
          >
            <Languages className="w-3 h-3" />
            {currentTranslitMeta.short}
            <ChevronDown className="w-2.5 h-2.5" />
          </button>
          {langPickerOpen && (
            <div
              className="absolute top-full right-0 mt-1 rounded-lg border border-slate-200 bg-white shadow-lg py-1 max-h-72 overflow-auto z-30"
              style={{ minWidth: 210 }}
              data-testid={`${testIdPrefix}-translit-picker`}
            >
              {TRANSLIT_LANGS.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => { setTranslitLang(l.code); setLangPickerOpen(false); }}
                  className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 ${translitLang === l.code ? "font-semibold" : ""}`}
                  data-testid={`${testIdPrefix}-translit-picker-${l.code}`}
                >
                  <span className="opacity-60 mr-1.5 text-[10px] uppercase tracking-widest">{l.short}</span>
                  <span className="mr-1.5">{l.native}</span>
                  {l.code !== "off" && (
                    <span className="opacity-40 text-[10px]">· {l.label}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <EditorContent
        editor={editor}
        className="pub-prose min-h-[400px] px-6 py-6"
        data-testid={`${testIdPrefix}-content`}
      />
    </div>
  );
}
